import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Close, DeleteOutline } from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { stopWheelNudge } from '../utils/numberInput';
import DuplicateSessionConfirmDialog from './DuplicateSessionConfirmDialog';
import { DuplicateEntry, DuplicateSessionInfo, dupKey, getDuplicateSessionError } from '../utils/duplicateSession';
import {
  ACCENT,
  BrandLocation,
  Loading,
  LocationChecks,
  LocationProblem,
  ProblemsPanel,
  ResultPanel,
  addDays,
  istDate,
  messageOf,
  plural,
  problemsOf,
  todayKey,
  useBrandLocations,
  useLocationData,
} from './multiLocation/shared';

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  CREATE SESSIONS — one form for one session, a repeating range, and several
 *  locations
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Replaces three dialogs that overlapped: "Create Session" (one date), "Bulk
 * Create" (a date range at one location) and "At several locations" (one week
 * at several). A single session is a range whose end date is its start date,
 * so one form covers all three:
 *
 *   Where   — the brand's locations, this one ticked (brands only; a
 *             single-location studio never sees it)
 *   What    — the class (each location uses ITS copy: linked copy first, then
 *             the same name), and optionally an instructor and a price
 *   When    — from / to dates, the times each day, the days of the week
 *
 * Sent the cheapest way that can carry it: one location with no instructor or
 * price change goes to the range endpoint (as Bulk Create did); otherwise the
 * sessions are listed and sent to one location, or to several in one request.
 */

const DAYS = [
  { label: 'Sun', value: 0 },
  { label: 'Mon', value: 1 },
  { label: 'Tue', value: 2 },
  { label: 'Wed', value: 3 },
  { label: 'Thu', value: 4 },
  { label: 'Fri', value: 5 },
  { label: 'Sat', value: 6 },
];

const DEFAULT_TIME = '09:00';
/** One hour after the last time (09:30 → 10:30), capped at 23:59. */
const nextTime = (last: string | undefined): string => {
  if (!last) return DEFAULT_TIME;
  const [h, m] = last.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return DEFAULT_TIME;
  const total = Math.min(h * 60 + m + 60, 23 * 60 + 59);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

/** IST "YYYY-MM-DDTHH:mm" for now — times at or before it have passed. */
const nowKey = () => new Date(Date.now() + 5.5 * 3600000).toISOString().slice(0, 16);
const familyKey = (c: any) =>
  c?.siblingGroup ? `g:${String(c.siblingGroup)}` : `n:${String(c?.className ?? '').trim().toLowerCase()}`;
const dayOf = (dateKey: string) => new Date(`${dateKey}T00:00:00`).getDay();

/** Per request, per location — see SessionsAtLocationDto. */
const MAX_PER_LOCATION = 1100;

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** The location the page is about; also finds the brand's other locations. */
  studioId: string;
  /** Locations ticked when the form opens. Defaults to [studioId]. */
  preselect?: string[];
  defaultStartDate?: string;
}

const CreateSessionsDialog: React.FC<Props> = ({ open, onClose, onSuccess, studioId, preselect, defaultStartDate }) => {
  const { creatable, loading: locLoading } = useBrandLocations(studioId);
  // Locations are offered only when there are two or more to choose from.
  const multi = creatable.length >= 2;
  const data = useLocationData();
  const { load } = data;

  const [selected, setSelected] = useState<string[]>([]);
  const [family, setFamily] = useState('');
  const [instructor, setInstructor] = useState('');
  const [price, setPrice] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [times, setTimes] = useState<string[]>([DEFAULT_TIME]);
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([1, 2, 3, 4, 5]);
  const [slots, setSlots] = useState('10');
  const [isLive, setIsLive] = useState(true);
  const [onlyForFemales, setOnlyForFemales] = useState(false);
  const [isTrialEnabled, setIsTrialEnabled] = useState(false);
  const [trialPrice, setTrialPrice] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [problems, setProblems] = useState<LocationProblem[] | null>(null);
  const [dup, setDup] = useState<DuplicateSessionInfo | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [result, setResult] = useState<{ created: any[]; failed: any[] } | null>(null);

  useEffect(() => {
    if (!open) return;
    const start = defaultStartDate && defaultStartDate >= todayKey() ? defaultStartDate : todayKey();
    setFamily(''); setInstructor(''); setPrice('');
    setStartDate(start); setEndDate(start);
    setTimes([DEFAULT_TIME]); setDaysOfWeek([1, 2, 3, 4, 5]); setSlots('10');
    setIsLive(true); setOnlyForFemales(false); setIsTrialEnabled(false); setTrialPrice('');
    setSaving(false); setError(null); setProblems(null); setDup(null); setDone(null); setResult(null);
  }, [open, defaultStartDate]);

  // Which locations are ticked. A single-location studio is always "here".
  useEffect(() => {
    if (!open || locLoading) return;
    if (!multi) { setSelected(studioId ? [studioId] : []); return; }
    const want = preselect ?? (studioId ? [studioId] : []);
    setSelected(want.filter((id) => creatable.some((l) => l._id === id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, locLoading, multi, creatable.length, studioId]);

  useEffect(() => { if (open && selected.length) load(selected); }, [open, selected, load]);

  const chosen: BrandLocation[] = multi
    ? creatable.filter((l) => selected.includes(l._id))
    : selected.map((id) => ({ _id: id, name: '', studioName: '', isActive: true, editable: true }));
  const single = chosen.length === 1;
  const loadingData = data.isLoading(selected);

  // One entry per "same class" across the ticked locations.
  const families = useMemo(() => {
    const map = new Map<string, { key: string; name: string; at: Record<string, any> }>();
    chosen.forEach((l) => {
      (data.get(l._id)?.classes ?? []).forEach((c: any) => {
        if (c.isManualCheckin) return;
        const k = familyKey(c);
        const e = map.get(k) ?? { key: k, name: c.className, at: {} as Record<string, any> };
        if (!e.at[l._id]) e.at[l._id] = c;
        map.set(k, e);
      });
    });
    return Array.from(map.values()).sort(
      (a, b) => Object.keys(b.at).length - Object.keys(a.at).length || a.name.localeCompare(b.name),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosen.map((l) => l._id).join(','), data]);

  const fam = families.find((f) => f.key === family);
  const classAt = (sid: string): any | null => {
    if (!fam) return null;
    if (fam.at[sid]) return fam.at[sid];
    const name = fam.name.trim().toLowerCase();
    return (data.get(sid)?.classes ?? []).find((c: any) => String(c.className).trim().toLowerCase() === name) ?? null;
  };
  const missingAt = fam ? chosen.filter((l) => !classAt(l._id)) : [];

  // Instructors at any ticked location; one chosen here applies only where
  // they teach — elsewhere the class's own instructor is kept.
  const instructors = useMemo(
    () =>
      Array.from(
        new Map(
          chosen.flatMap((l) => data.get(l._id)?.instructors ?? []).map((i: any) => [i._id, i] as [string, any]),
        ).values(),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chosen.map((l) => l._id).join(','), data],
  );
  const instructorAt = (sid: string) =>
    instructor && (data.get(sid)?.instructors ?? []).some((i: any) => i._id === instructor) ? instructor : '';

  const isRange = !!startDate && !!endDate && endDate > startDate;
  const dates = useMemo(() => {
    if (!startDate || !endDate || endDate < startDate) return [] as string[];
    const out: string[] = [];
    for (let d = startDate; d <= endDate && out.length < 400; d = addDays(d, 1)) {
      if (!isRange || daysOfWeek.includes(dayOf(d))) out.push(d);
    }
    return out;
  }, [startDate, endDate, isRange, daysOfWeek]);

  const validTimes = times.filter(Boolean);
  const slotCount = parseInt(slots, 10);

  // The sessions each location gets — past times are left out (and reported).
  const plan = useMemo(() => {
    const now = nowKey();
    const past = dates.reduce((n, d) => n + validTimes.filter((t) => `${d}T${t}` <= now).length, 0);
    const byLoc = chosen.map((l) => {
      const cls = classAt(l._id);
      const ins = instructorAt(l._id);
      const sessions: any[] = [];
      if (cls) {
        dates.forEach((d) =>
          validTimes.forEach((t) => {
            if (`${d}T${t}` <= now) return;
            sessions.push({
              class: cls._id,
              date: istDate(d, t),
              slots: slotCount >= 1 ? slotCount : cls.seatLayout ? 0 : NaN,
              isLive,
              onlyForFemales,
              isTrialEnabled,
              trialPrice: isTrialEnabled ? parseFloat(trialPrice) || 0 : 0,
              ...(price !== '' ? { price: parseFloat(price) } : {}),
              ...(ins ? { instructor: ins } : {}),
            });
          }),
        );
      }
      return { studioId: l._id, name: l.name, sessions };
    });
    return { byLoc, past };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosen.map((l) => l._id).join(','), fam, dates, validTimes.join(','), slotCount, isLive, onlyForFemales, isTrialEnabled, trialPrice, price, instructor, data]);

  const total = plan.byLoc.reduce((n, l) => n + l.sessions.length, 0);

  const issues: string[] = (() => {
    const out: string[] = [];
    if (!chosen.length) out.push('a location');
    if (!fam) out.push('a class');
    if (fam && missingAt.length) out.push(`the class at ${missingAt.map((l) => l.name).join(', ')}`);
    if (!startDate || !endDate || endDate < startDate) out.push('valid dates');
    if (isRange && !daysOfWeek.length) out.push('at least one day');
    if (!validTimes.length) out.push('a time');
    const needSlots = !fam || chosen.some((l) => !classAt(l._id)?.seatLayout);
    if (needSlots && !(slotCount >= 1)) out.push('the slots');
    if (isTrialEnabled && (trialPrice === '' || Number(trialPrice) < 0)) out.push('the trial price');
    if (price !== '' && Number(price) < 0) out.push('a price of 0 or more');
    if (plan.byLoc.some((l) => l.sessions.length > MAX_PER_LOCATION)) out.push(`at most ${MAX_PER_LOCATION} sessions per location (shorten the dates)`);
    return out;
  })();

  const finish = (message: string) => {
    setDone(message);
    onSuccess();
    setTimeout(onClose, 1200);
  };

  const send = async (allowDuplicate: boolean, skip: DuplicateEntry[] = []) => {
    setSaving(true); setError(null); setProblems(null);
    const skipKeys = new Set(skip.map((d) => dupKey(d.classId, d.date)));
    try {
      const only = plan.byLoc[0];
      const cls = only ? classAt(only.studioId) : null;
      if (single && cls && !instructor && price === '') {
        // One location, the class's own instructor and price — the range
        // endpoint (what Bulk Create used), which also reaches a full year.
        const out: any = await apiService.bulkCreateByRange(only.studioId, {
          class: cls._id,
          startDate,
          endDate,
          times: validTimes,
          slots: slotCount >= 1 ? slotCount : 0,
          isLive,
          onlyForFemales,
          daysOfWeek: isRange ? daysOfWeek : [0, 1, 2, 3, 4, 5, 6],
          isTrialEnabled,
          trialPrice: isTrialEnabled ? parseFloat(trialPrice) || 0 : 0,
          ...(allowDuplicate ? { allowDuplicate: true } : {}),
          ...(skip.length ? { skip: skip.map((d) => ({ class: d.classId, date: d.date })) } : {}),
        });
        setDup(null);
        finish(out?.count > 0 ? `${plural(out.count, 'session')} created.` : 'No sessions created — every one was skipped.');
      } else if (single && only) {
        const sessions = only.sessions.filter((p) => !skipKeys.has(dupKey(p.class, p.date)));
        if (!sessions.length) { setDup(null); finish('No sessions created — every one was skipped.'); return; }
        await apiService.createMultipleSessions(only.studioId, { sessions, intent: 'create', ...(allowDuplicate ? { allowDuplicate: true } : {}) });
        setDup(null);
        finish(`${plural(sessions.length, 'session')} created.`);
      } else {
        const locations = plan.byLoc
          .map((l) => ({ studioId: l.studioId, sessions: l.sessions.filter((p) => !skipKeys.has(dupKey(p.class, p.date))) }))
          .filter((l) => l.sessions.length);
        if (!locations.length) { setDup(null); finish('No sessions created — every one was skipped.'); return; }
        const out: any = await apiService.createSessionsAtLocations({ locations, allowDuplicate });
        setDup(null);
        setResult(out);
        if (!out.failed?.length) onSuccess();
      }
    } catch (e: any) {
      const d = getDuplicateSessionError(e);
      const p = problemsOf(e);
      if (d) setDup(d);
      else if (p) { setDup(null); setProblems(p); }
      else { setDup(null); setError(messageOf(e, 'The sessions could not be created.')); }
    } finally {
      setSaving(false);
    }
  };

  const busy = saving || !!done;
  const handleClose = () => { if (!saving) onClose(); };
  const btnSx = { bgcolor: ACCENT, '&:hover': { bgcolor: '#d45180' } };
  const cls1 = single ? classAt(chosen[0]._id) : null;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
        <DialogTitle sx={{ pr: 6 }}>
          <Typography variant="h6" fontWeight={700}>Create Sessions</Typography>
          <IconButton onClick={handleClose} disabled={saving} sx={{ position: 'absolute', right: 12, top: 12 }} aria-label="Close">
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {result ? (
            <ResultPanel
              created={result.created.map((c: any) => ({ location: c.location, label: `${plural(c.count, 'session')} created` }))}
              failed={result.failed}
            />
          ) : locLoading ? (
            <Loading label="Loading your locations…" />
          ) : (
            <Box display="flex" flexDirection="column" gap={2.25}>
              {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
              {done && <Alert severity="success">{done}</Alert>}
              {problems && <ProblemsPanel problems={problems} />}

              {/* Where — brands only. */}
              {multi && (
                <LocationChecks locations={creatable} selected={selected} onChange={(ids) => { setSelected(ids); setProblems(null); }}
                  severalHint="Each location gets the same schedule, using its own copy of the class." />
              )}

              {chosen.length === 0 ? null : loadingData ? (
                <Loading label="Loading classes and instructors…" />
              ) : (
                <>
                  {/* What */}
                  <FormControl fullWidth size="small" error={!!fam && missingAt.length > 0}>
                    <InputLabel>Class *</InputLabel>
                    <Select value={family} label="Class *" onChange={(e) => { setFamily(e.target.value); setProblems(null); }}>
                      {families.length === 0 && <MenuItem disabled value="">No classes here yet</MenuItem>}
                      {families.map((f) => {
                        const n = chosen.filter((l) => f.at[l._id]).length;
                        const c = single ? f.at[chosen[0]._id] : null;
                        return (
                          <MenuItem key={f.key} value={f.key}>
                            <Box display="flex" justifyContent="space-between" width="100%" gap={2}>
                              <span>{f.name}{c ? ` · ₹${c.price} · ${c.duration} min` : ''}</span>
                              {!single && (
                                <Typography variant="caption" color={n === chosen.length ? 'success.main' : 'warning.main'}>
                                  {n === chosen.length ? 'at all locations' : `at ${n} of ${chosen.length}`}
                                </Typography>
                              )}
                            </Box>
                          </MenuItem>
                        );
                      })}
                    </Select>
                    {fam && missingAt.length > 0 && (
                      <FormHelperText>
                        {missingAt.map((l) => l.name).join(', ')} {missingAt.length === 1 ? 'has' : 'have'} no "{fam.name}". Untick {missingAt.length === 1 ? 'it' : 'them'}, or create the class there first.
                      </FormHelperText>
                    )}
                  </FormControl>

                  <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
                    <FormControl fullWidth size="small">
                      <InputLabel shrink>Instructor</InputLabel>
                      <Select displayEmpty notched label="Instructor" value={instructors.some((i: any) => i._id === instructor) ? instructor : ''}
                        onChange={(e) => setInstructor(e.target.value)}>
                        <MenuItem value="">
                          <em>{cls1?.instructor?.name ? `Class instructor (${cls1.instructor.name})` : "The class's instructor"}</em>
                        </MenuItem>
                        {instructors.map((i: any) => <MenuItem key={i._id} value={i._id}>{i.name}</MenuItem>)}
                      </Select>
                      {!single && instructor && <FormHelperText>Used where they teach; elsewhere the class's instructor.</FormHelperText>}
                    </FormControl>
                    <TextField label="Price (₹)" type="number" size="small" fullWidth value={price} onWheel={stopWheelNudge}
                      placeholder={cls1 ? String(cls1.price) : 'Class price'} InputLabelProps={{ shrink: true }}
                      onChange={(e) => setPrice(e.target.value)} helperText="Leave empty to use the class price." />
                  </Box>

                  <Divider />

                  {/* When */}
                  <Box display="flex" gap={2}>
                    <TextField label="From *" type="date" size="small" fullWidth value={startDate} InputLabelProps={{ shrink: true }}
                      inputProps={{ min: todayKey() }}
                      onChange={(e) => { const v = e.target.value; setStartDate(v); if (!endDate || endDate < v) setEndDate(v); }} />
                    <TextField label="To *" type="date" size="small" fullWidth value={endDate} InputLabelProps={{ shrink: true }}
                      inputProps={{ min: startDate || todayKey() }} onChange={(e) => setEndDate(e.target.value)} />
                  </Box>

                  {isRange && (
                    <Box>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body2" fontWeight={600}>Days *</Typography>
                        <Box display="flex" gap={1}>
                          <Button size="small" onClick={() => setDaysOfWeek([0, 1, 2, 3, 4, 5, 6])} sx={{ minWidth: 0, p: 0.5, fontSize: 11 }}>All</Button>
                          <Button size="small" onClick={() => setDaysOfWeek([])} sx={{ minWidth: 0, p: 0.5, fontSize: 11 }}>Clear</Button>
                        </Box>
                      </Box>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        {DAYS.map((d) => {
                          const on = daysOfWeek.includes(d.value);
                          return (
                            <Chip key={d.value} label={d.label} size="small" sx={{ cursor: 'pointer' }}
                              color={on ? 'primary' : 'default'} variant={on ? 'filled' : 'outlined'}
                              onClick={() => setDaysOfWeek((p) => (on ? p.filter((x) => x !== d.value) : [...p, d.value].sort()))} />
                          );
                        })}
                      </Box>
                    </Box>
                  )}

                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2" fontWeight={600}>Times *</Typography>
                      <Button size="small" startIcon={<Add sx={{ fontSize: 14 }} />} onClick={() => setTimes((t) => [...t, nextTime(t[t.length - 1])])}
                        sx={{ minWidth: 0, p: 0.5, fontSize: 11 }}>
                        Add time
                      </Button>
                    </Box>
                    <Box display="flex" flexDirection="column" gap={1}>
                      {times.map((t, i) => (
                        <Box key={i} display="flex" alignItems="center" gap={1}>
                          <TextField type="time" size="small" value={t} sx={{ flex: 1 }} inputProps={{ step: 300 }}
                            onChange={(e) => setTimes((all) => all.map((x, j) => (j === i ? e.target.value : x)))} />
                          {times.length > 1 && (
                            <IconButton size="small" color="error" onClick={() => setTimes((all) => all.filter((_, j) => j !== i))}>
                              <DeleteOutline sx={{ fontSize: 18 }} />
                            </IconButton>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  <TextField label="Slots per session *" type="number" size="small" fullWidth value={slots} onWheel={stopWheelNudge}
                    placeholder={cls1?.seatLayout ? 'From the room' : ''} inputProps={{ min: 1 }} onChange={(e) => setSlots(e.target.value)} />

                  <Divider />

                  <Box display="flex" gap={3} flexWrap="wrap">
                    <FormControlLabel control={<Switch size="small" checked={isLive} onChange={(e) => setIsLive(e.target.checked)} />}
                      label={<Typography variant="body2">Live (members can book)</Typography>} />
                    <FormControlLabel control={<Switch size="small" checked={onlyForFemales} onChange={(e) => setOnlyForFemales(e.target.checked)} />}
                      label={<Typography variant="body2">Women only</Typography>} />
                  </Box>
                  <Box>
                    <FormControlLabel
                      control={<Switch size="small" checked={isTrialEnabled} onChange={(e) => { setIsTrialEnabled(e.target.checked); if (!e.target.checked) setTrialPrice(''); }} />}
                      label={<Typography variant="body2">Trial session</Typography>} />
                    {isTrialEnabled && (
                      <TextField label="Trial price (₹) *" type="number" size="small" fullWidth value={trialPrice} onWheel={stopWheelNudge}
                        sx={{ mt: 1.5 }} inputProps={{ min: 0 }} onChange={(e) => setTrialPrice(e.target.value)} helperText="Use 0 for a free trial." />
                    )}
                  </Box>

                  {total > 0 && (
                    <Alert severity="info" icon={false}>
                      <Typography variant="body2">
                        <strong>{plural(total, 'session')}</strong>
                        {!single && <> across <strong>{plural(plan.byLoc.filter((l) => l.sessions.length).length, 'location')}</strong></>}
                        {' '}— {plural(dates.length, 'day')} × {plural(validTimes.length, 'time')} ({validTimes.join(', ')})
                        {plan.past > 0 && <>. {plural(plan.past, 'time')} already passed and {plan.past === 1 ? 'is' : 'are'} left out.</>}
                      </Typography>
                    </Alert>
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          {result ? (
            <Button variant="contained" onClick={onClose} sx={btnSx}>Done</Button>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" flex={1}>
                {!done && issues.length ? `Still needed: ${issues.join(', ')}` : ''}
              </Typography>
              <Button onClick={handleClose} disabled={saving}>{done ? 'Close' : 'Cancel'}</Button>
              {!done && (
                <Button variant="contained" sx={btnSx} disabled={busy || loadingData || issues.length > 0 || total === 0}
                  onClick={() => send(false)} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}>
                  {saving ? 'Creating…' : `Create ${total > 0 ? plural(total, 'session') : 'sessions'}`}
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>

      <DuplicateSessionConfirmDialog
        open={!!dup}
        info={dup}
        loading={saving}
        totalRequested={total}
        onCancel={() => setDup(null)}
        onConfirm={(skip) => send(true, skip)}
      />
    </>
  );
};

export default CreateSessionsDialog;
